/**
 * Módulo 21 na tela — a porta para `/painel/gestor` e o vocabulário da fila.
 *
 * Os pesos NÃO moram aqui: vêm na resposta do servidor (`regra.pesos`), que é
 * quem soma os pontos. A tela só traduz o nome de cada sinal para a frase da
 * premissa; se o servidor mudar um peso, a tela mostra o peso novo sem
 * ninguém lembrar de mudar dois lugares.
 */
import { somarDias } from '@/lib/dia-da-operacao';
import { validarBuscaDePeriodo, type BuscaDePeriodo } from '@/lib/busca-de-periodo';
import { formatarDecimal, formatarDia, formatarReais, formatarReaisCompacto } from '@/lib/formato';
import { pedir } from '@/lib/pedido';

export type Sinal =
  | 'preventivaVencida'
  | 'ordemComMaquinaParada'
  | 'ordemAberta'
  | 'divergenciaAberta'
  | 'consumoForaDaFaixa'
  | 'turnoParadoEvitavel'
  | 'custoForaDaCurva';

export interface ItemDaFila {
  readonly equipamentoId: string;
  readonly codigo: string;
  readonly descricao: string;
  readonly pontos: number;
  readonly motivos: readonly { readonly sinal: Sinal; readonly pontos: number; readonly frase: string }[];
  readonly custoPorHora: number | null;
}

/** O recorte diário (0091): hoje ou ontem, no fuso da operação, resolvido no servidor. */
export type RecorteDoDia = 'hoje' | 'ontem';

/**
 * O dia da R06 (p.72): com "Dia", as duas datas viram um campo só — hoje,
 * ontem ou qualquer data `aaaa-mm-dd`.
 */
export type DiaDoGestor = RecorteDoDia | `${number}-${number}-${number}`;

const ehData = (v: unknown): v is `${number}-${number}-${number}` => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);

/** Como a fila se lê: por máquina, ou agrupada por obra ou por centro de custo (0091). */
export type EixoDaFila = 'maquina' | 'obra' | 'centro';

/**
 * Um grupo da fila — obra ou centro de custo: a soma dos pontos das máquinas
 * alocadas nele agora, e o porquê somado por sinal. Nenhum ponto novo: é a
 * mesma fila, agrupada no servidor (`agruparPrioridades`).
 */
export interface GrupoDaFila {
  /** Nulo no grupo das máquinas sem obra (ou sem centro de custo). */
  readonly id: string | null;
  readonly codigo: string | null;
  readonly nome: string;
  readonly pontos: number;
  readonly motivos: ItemDaFila['motivos'];
  readonly maquinas: readonly MaquinaDoGrupo[];
  /**
   * R06 (p.72): a soma do custo parado por dia das máquinas do grupo com custo
   * por hora; nula quando nenhuma tem. Ausente em servidor anterior.
   */
  readonly custoParadoPorDia?: number | null;
  /** Quantas máquinas do grupo ficaram fora da soma, sem custo por hora. */
  readonly maquinasSemCustoPorHora?: number;
}

export interface MaquinaDoGrupo {
  readonly equipamentoId: string;
  readonly codigo: string;
  readonly pontos: number;
  /** R06 (p.72): os porquês da máquina, para o subitem. Ausente em servidor anterior. */
  readonly motivos?: readonly string[];
  readonly custoParadoPorDia?: number | null;
  /** A situação de agora, quando Parada ou Em manutenção, e desde quando. */
  readonly parada?: { readonly status: string; readonly desde: string } | null;
}

export interface VisaoDoGestor {
  readonly de: string;
  readonly ate: string;
  /** Ausente em servidor anterior à 0091: vale como período. `dia` é uma data escolhida (R06). */
  readonly recorte?: RecorteDoDia | 'periodo' | 'dia';
  readonly criterio: string;
  readonly sinais: { readonly preventivas: boolean; readonly ordens: boolean; readonly operacao: boolean; readonly combustivel: boolean };
  readonly executivo: {
    readonly custoTotal: number;
    readonly semPreco: number;
    /** 0095: a mão de obra pelo valor-hora da função, já dentro de `custoTotal`. Ausente em servidor anterior. */
    readonly maoDeObra?: number;
    /** As horas de pessoa sem valor, fora do total. */
    readonly horasMaoDeObraSemValor?: number;
    /** A frase do servidor sobre essas horas; nula quando não há. */
    readonly maoDeObraSemValor?: string | null;
    readonly horasMotor: number;
    readonly horasProducao: number | null;
    /** Custo das máquinas COM hora de motor dividido pelas horas delas. */
    readonly custoPorHoraMotor: number | null;
    /** O custo das máquinas sem hora de motor: fica fora da divisão, e a tela o diz. */
    readonly custoSemHoraMotor: number;
    /** Custo das máquinas COM hora de produção dividido pelas horas delas. */
    readonly custoPorHoraProdutiva: number | null;
    /** O custo das máquinas sem hora de produção. Nulo quando o perfil não lê a operação. */
    readonly custoSemHoraProducao: number | null;
    readonly horasParadasEvitaveis: number | null;
    readonly divergenciasAbertas: number | null;
    readonly horasAExplicar: number | null;
  };
  /**
   * O periodo anterior, de mesma duracao, para a variacao dos cartoes (R02,
   * p.31). Vem como TOTAIS: quem diz se subir e bom ou ruim e a tela.
   *
   * `divergenciasAbertas` nao esta aqui de proposito — ela conta o que esta
   * aberto AGORA, nao o que aconteceu no periodo, e compara-la com ela mesma
   * daria sempre zero com cara de medicao.
   */
  readonly anterior: {
    readonly de: string;
    readonly ate: string;
    readonly custoTotal: number;
    readonly maoDeObra?: number;
    readonly custoPorHoraProdutiva: number | null;
    readonly horasParadasEvitaveis: number | null;
  };
  readonly regra: {
    readonly pesos: Readonly<Record<Sinal, number>>;
    readonly horasDoTurno: number;
    readonly fatorForaDaCurva: number;
  };
  readonly medianaDoCustoPorHora: number | null;
  readonly maquinasAtivas: number;
  readonly fila: readonly ItemDaFila[];
  /** Ausentes em servidor anterior à 0091. */
  readonly filaPorObra?: readonly GrupoDaFila[];
  readonly filaPorCentroDeCusto?: readonly GrupoDaFila[];
  /** R06 (p.72): a definição do custo parado, e por quantos dias ele divide. */
  readonly custoParado?: { readonly premissa: string; readonly dias: number };
}

/* ------------------------------------- ferramenta 3 · custo, receita e lucro
 *
 * O v6 pede o resultado "com PREMISSAS VISIVEIS", e é por isso que nada aqui
 * se chama lucro: o nome é `resultadoParcial`, e as premissas vêm no MESMO
 * objeto do número — separadas, a tela poderia mostrar um e esquecer o outro,
 * e é sempre a nota de rodapé que se esquece.
 */

export interface ResultadoDoContrato {
  readonly contratoId: string;
  readonly contrato: string;
  readonly cliente: string;
  readonly receita: number;
  readonly custoDireto: number;
  /* 0096 · ausentes em servidor anterior */
  readonly depreciacao?: number;
  readonly indiretoRateado?: number;
  /** Direto + depreciação + indireto rateado: o custo que o resultado usa. */
  readonly custoTotal?: number;
  /** Quanto do custo total veio de máquina dividida com outro contrato, pela hora. */
  readonly custoDividido?: number;
  /** `receita - custoTotal`; parcial enquanto `premissas.completo` for falso. */
  readonly resultadoParcial: number;
  /** Nulo quando a receita é zero — ver `margemEscrita`. */
  readonly margemParcial: number | null;
  readonly horasReconhecidas: number;
  readonly maquinas: number;
}

/** 0096 · a máquina de vários contratos que foi dividida pela hora. */
export interface MaquinaDividida {
  readonly equipamentoId: string;
  readonly codigo: string;
  readonly custo: number;
  readonly base: 'horas_reconhecidas' | 'horas_apontadas';
  readonly partes: readonly { readonly contratoId: string; readonly contrato: string; readonly valor: number }[];
}

/** Por que o custo de uma máquina não foi atribuído a contrato nenhum. */
export type MotivoNaoAtribuido = 'varios_contratos' | 'sem_contrato';

export interface ResultadoDaCompetencia {
  readonly competencia: string;
  /** 0098 · `false` (ou ausente, no servidor de antes): o custo é o da empresa inteira. */
  readonly soReceita?: false;
  readonly contratos: readonly ResultadoDoContrato[];
  readonly custoNaoAtribuido: {
    readonly valor: number;
    readonly maquinas: readonly {
      readonly equipamentoId: string;
      readonly codigo: string;
      readonly custo: number;
      readonly motivo: MotivoNaoAtribuido;
      readonly contratos: number;
    }[];
  };
  readonly premissas: {
    readonly inclui: readonly string[];
    readonly naoInclui: readonly string[];
    readonly completo: boolean;
  };
  /** 0096 · as máquinas de vários contratos divididas pela hora. */
  readonly custoDividido?: { readonly valor: number; readonly maquinas: readonly MaquinaDividida[] };
  readonly totais: {
    readonly receita: number;
    readonly custoDireto: number;
    readonly depreciacao?: number;
    readonly indiretoRateado?: number;
    readonly custoTotal?: number;
    readonly resultadoParcial: number;
    readonly margemParcial: number | null;
  };
  readonly janela: { readonly de: string; readonly ate: string };
  readonly criterioDeCusteio: string;
  readonly criterioDeRateio?: string;
}

/**
 * 0098 · O RESULTADO DE QUEM NÃO VÊ A EMPRESA INTEIRA (papel de filial, obra
 * ou contrato): só a receita dos contratos visíveis, inteira, e o porquê nas
 * premissas (`gestor/resultado.ts`). Margem sobre o custo só das máquinas do
 * alcance seria um número que não é de ninguém.
 */
export interface ResultadoSoDaReceita {
  readonly competencia: string;
  readonly soReceita: true;
  readonly contratos: readonly {
    readonly contratoId: string;
    readonly contrato: string;
    readonly cliente: string;
    readonly receita: number;
    readonly horasReconhecidas: number;
  }[];
  readonly premissas: { readonly inclui: readonly string[]; readonly naoInclui: readonly string[]; readonly completo: boolean };
  readonly totais: { readonly receita: number };
}

export type RespostaDoResultado = ResultadoDaCompetencia | ResultadoSoDaReceita;

export function ehSoReceita(r: RespostaDoResultado): r is ResultadoSoDaReceita {
  return r.soReceita === true;
}

/**
 * O BLOCO DO RESULTADO SÓ SE PEDE COM AS DUAS LICENÇAS (robôs no QAS,
 * 20/10/2026): `custo.ler` abre a visão do gestor, mas o resultado cruza custo
 * e receita, e o servidor recusa sem `receita.ler`. O gestor operacional e o
 * planejador abriam a tela e recebiam "Não foi feito". Sem a licença, a seção
 * mostra o estado sem permissão desenhado (p.34), e nada é pedido.
 * O celular segue a mesma regra (`TelaCustos`, `enabled: podeLer && veReceita`).
 */
export function pedeOResultado(permissoes: readonly string[] | undefined): boolean {
  return permissoes !== undefined && permissoes.includes('custo.ler') && permissoes.includes('receita.ler');
}

export const apiDoGestor = {
  visao: (de: string, ate: string) =>
    pedir<VisaoDoGestor>(`/painel/gestor?de=${encodeURIComponent(de)}&ate=${encodeURIComponent(ate)}`),
  /** O dia (hoje, ontem ou uma data) é resolvido no servidor, no fuso da operação: a mesa e o celular leem o mesmo dia. */
  visaoDoDia: (dia: DiaDoGestor) => pedir<VisaoDoGestor>(`/painel/gestor?dia=${encodeURIComponent(dia)}`),
  resultado: (competencia: string) =>
    pedir<RespostaDoResultado>(`/painel/gestor/resultado?competencia=${encodeURIComponent(competencia)}`),
};

/**
 * A margem escrita, e o nulo dito por extenso.
 *
 * Receita zero não é margem zero: o contrato sem medição aprovada não tem
 * margem ruim — ele não tem medição, e "0%" o acusaria de algo que ele não
 * fez. Um travessão também não serve, porque se lê como falta de dado.
 */
export function margemEscrita(margem: number | null): string {
  if (margem === null) return 'sem medição aprovada';
  return `${formatarDecimal(margem, 1)}%`;
}

/** Por que aquela máquina ficou de fora, e o que resolve cada caso. */
export function motivoNaoAtribuidoEscrito(motivo: MotivoNaoAtribuido): string {
  /* 0096: a máquina de vários contratos se divide pela hora; só cai aqui a
     que não teve hora reconhecida nem apontada em nenhum deles. */
  if (motivo === 'varios_contratos') return 'serviu mais de um contrato no mês, sem hora para dividir';
  return 'não tem alocação a contrato no mês';
}

export function oQueResolveONaoAtribuido(motivo: MotivoNaoAtribuido): string {
  if (motivo === 'varios_contratos') {
    return 'Apontar o turno da máquina em cada contrato, ou conciliar as horas reconhecidas, em Operação';
  }
  return 'Cadastrar a alocação da máquina ao contrato, em Equipamentos';
}

/** A base com que a máquina de vários contratos foi dividida. */
export function baseDaDivisaoEscrita(base: MaquinaDividida['base']): string {
  return base === 'horas_reconhecidas' ? 'pelas horas reconhecidas de cada contrato' : 'pelas horas apontadas em cada contrato';
}

/**
 * A frase que acompanha o resultado, e ela NÃO é opcional.
 *
 * O risco que o v6 escreve para o módulo 11 é "chamar custo parcial de custo
 * total ou margem final", e o painel executivo é onde essa mentira custa mais:
 * o dono lê o número e decide. Então o resultado nunca aparece sem esta linha.
 */
export function porqueOResultadoEParcial(r: ResultadoDaCompetencia): string {
  const quantas = r.premissas.naoInclui.length;
  if (quantas === 0 && r.premissas.completo) {
    /* Alcançável desde a 0096: depreciação e rateio existem, e a lista só tem
       o que falta nos dados. Vazia, o resultado é completo — e a frase diz o
       que ele inclui, sem chamar nada de lucro. */
    return 'Resultado do mês completo: receita aprovada menos o custo total das máquinas — direto, depreciação e custo indireto rateado. As premissas estão abaixo.';
  }
  if (quantas === 0) return 'Resultado do mês, com as premissas que o servidor declarou.';
  return quantas === 1
    ? 'Resultado PARCIAL: 1 coisa não entrou na conta, e ela está listada abaixo.'
    : `Resultado PARCIAL: ${String(quantas)} coisas não entraram na conta, e elas estão listadas abaixo.`;
}

/** O que cada sinal quer dizer, na ordem de gravidade da regra. */
export const PREMISSA_DO_SINAL: Readonly<Record<Sinal, (regra: VisaoDoGestor['regra']) => string>> = {
  preventivaVencida: () => 'por preventiva vencida — risco de quebra',
  ordemComMaquinaParada: () => 'por O.S. aberta com a máquina parada — indisponível agora',
  divergenciaAberta: () => 'por divergência de horas aberta — hora em disputa com o cliente',
  custoForaDaCurva: (r) =>
    `se o custo por hora passa de ${String(r.fatorForaDaCurva).replace('.', ',')} vez a mediana da frota`,
  ordemAberta: () => 'por O.S. aberta com a máquina em serviço — trabalho pendente',
  consumoForaDaFaixa: () => 'por abastecimento com consumo fora da faixa — desperdício ou defeito',
  turnoParadoEvitavel: (r) => `a cada ${String(r.horasDoTurno)} h paradas por causa evitável — um turno perdido`,
};

const NOME_DO_MODULO: Readonly<Record<keyof VisaoDoGestor['sinais'], string>> = {
  preventivas: 'manutenção',
  ordens: 'oficina',
  operacao: 'operação',
  combustivel: 'combustível',
};

/**
 * Os módulos cujos sinais ficaram FORA da conta, porque a pessoa não os lê.
 * A tela escreve isso ao lado da fila: uma fila que ignorou metade dos sinais
 * sem avisar ordena errado com cara de certa.
 */
export function modulosForaDaConta(sinais: VisaoDoGestor['sinais']): string[] {
  return (Object.keys(NOME_DO_MODULO) as (keyof VisaoDoGestor['sinais'])[])
    .filter((k) => !sinais[k])
    .map((k) => NOME_DO_MODULO[k]);
}

/*
 * O CUSTO POR HORA E O QUE FICOU FORA DELE (revisão R01/R02,
 * `painel-gestor-custo-hora-frota`). O servidor divide o custo das máquinas
 * COM hora pelas horas delas; o custo das máquinas sem hora não entra na
 * divisão e volta à parte. A tela escreve esse valor: sem ele, "R$ 0,00/h" ao
 * lado de um custo direto de milhares de reais se leria como erro de conta.
 */

/** O contexto curto do card de custo por hora produtiva (p.17). */
export function contextoDoCustoPorHoraProdutiva(e: VisaoDoGestor['executivo']): string {
  if (e.custoPorHoraProdutiva === null) return 'sem hora de produção apontada';
  return e.custoSemHoraProducao !== null && e.custoSemHoraProducao > 0
    ? `${formatarReais(e.custoSemHoraProducao)} sem hora, fora da conta`
    : 'por hora de produção apontada';
}

/** A definição dos indicadores, no texto logo abaixo da faixa. */
export function definicaoDoExecutivo(e: VisaoDoGestor['executivo']): string {
  const motor = e.custoPorHoraMotor === null ? 'não há hora apontada' : `${formatarReais(e.custoPorHoraMotor)}/h`;
  const fora = [
    e.custoPorHoraProdutiva !== null && e.custoSemHoraProducao !== null && e.custoSemHoraProducao > 0
      ? `${formatarReais(e.custoSemHoraProducao)} sem hora de produção`
      : null,
    e.custoPorHoraMotor !== null && e.custoSemHoraMotor > 0
      ? `${formatarReais(e.custoSemHoraMotor)} sem hora de motor`
      : null,
  ].filter((parte): parte is string => parte !== null);
  return [
    'Custo direto soma combustível, peças, lubrificante, terceiros e a mão de obra pelo valor-hora da função: é o custo direto, e a depreciação e o custo indireto rateado ficam no custo total, em Custos e no resultado por contrato.',
    /* A hora de pessoa sem valor (0095): a frase é do servidor, nunca daqui. */
    ...(e.maoDeObraSemValor ? [`${e.maoDeObraSemValor}.`] : []),
    `Custo por hora produtiva divide o custo das máquinas com hora de produção apontada pelas horas delas; por hora de motor, ${motor}.`,
    ...(fora.length > 0 ? [`O custo de máquina sem hora no período fica fora da divisão: ${fora.join(' e ')}.`] : []),
    'Paradas evitáveis são as de causa que a operação pode atacar, já encerradas.',
  ].join(' ');
}

/* ---------------------------------------------- o recorte e o eixo na URL
 *
 * A busca da Visão do gestor é a do período (a mesma de Custos) mais duas
 * escolhas desta tela: o dia (`?dia=hoje`) e o agrupamento da fila
 * (`?fila=obra`). Ficam na URL pelo mesmo motivo do período (p.16): "olha a
 * fila por obra de ontem" é um link que se manda.
 */
export interface BuscaDoGestor extends BuscaDePeriodo {
  readonly dia?: DiaDoGestor;
  readonly fila?: Exclude<EixoDaFila, 'maquina'>;
}

export function validarBuscaDoGestor(entrada: Record<string, unknown>): BuscaDoGestor {
  const dia = entrada['dia'];
  const fila = entrada['fila'];
  return {
    ...validarBuscaDePeriodo(entrada),
    ...(dia === 'hoje' || dia === 'ontem' || ehData(dia) ? { dia } : {}),
    ...(fila === 'obra' || fila === 'centro' ? { fila } : {}),
  };
}

/** Os grupos do eixo escolhido; nulo no eixo por máquina, que é a fila de sempre. */
export function gruposDaFila(v: VisaoDoGestor, eixo: EixoDaFila): readonly GrupoDaFila[] | null {
  if (eixo === 'maquina') return null;
  return (eixo === 'obra' ? v.filaPorObra : v.filaPorCentroDeCusto) ?? [];
}

/** "Dia 05/10/2026, comparado com 04/10/2026" — o recorte escrito, com a base da variação. */
export function recorteEscrito(v: Pick<VisaoDoGestor, 'de' | 'ate' | 'recorte' | 'anterior'>): string {
  if (v.recorte === 'hoje' || v.recorte === 'ontem') {
    return `${v.recorte === 'hoje' ? 'Hoje' : 'Ontem'}, ${formatarDia(v.de)}, comparado com ${formatarDia(v.anterior.de)}.`;
  }
  if (v.recorte === 'dia') return `Dia ${formatarDia(v.de)}, comparado com ${formatarDia(v.anterior.de)}.`;
  return `De ${formatarDia(v.de)} a ${formatarDia(v.ate)}, comparado com ${formatarDia(v.anterior.de)} a ${formatarDia(v.anterior.ate)}.`;
}

/** O nome do grupo e o que ele junta, para a primeira coluna e o cartão. */
export function maquinasDoGrupo(g: GrupoDaFila): string {
  const n = g.maquinas.length;
  return `${String(n)} ${n === 1 ? 'máquina' : 'máquinas'} na fila: ${g.maquinas.map((m) => m.codigo).join(', ')}`;
}

/** "R$ 4,1 mil/dia" — o custo parado do grupo (R06, p.72); "—" sem base. */
export function custoParadoEscrito(valor: number | null | undefined): string {
  if (valor === null || valor === undefined) return '—';
  return `${formatarReaisCompacto(valor)}/dia`;
}

/** Os dias inteiros desde o instante, para "Parada há 3 dias". */
function diasDesde(desde: string, agora: Date): number {
  return Math.max(0, Math.floor((agora.getTime() - Date.parse(desde)) / 86_400_000));
}

/**
 * A linha do subitem (R06, p.72): "Parada há 3 dias · preventiva vencida".
 * Sem parada agora, só os porquês.
 */
export function linhaDaMaquinaDoGrupo(m: MaquinaDoGrupo, agora: Date = new Date()): string {
  const parada = m.parada
    ? (() => {
        const n = diasDesde(m.parada.desde, agora);
        const tempo = n === 0 ? 'desde hoje' : n === 1 ? 'há 1 dia' : `há ${String(n)} dias`;
        return `${m.parada.status} ${tempo}`;
      })()
    : null;
  return [parada, ...(m.motivos ?? [])].filter(Boolean).join(' · ');
}

/** O dia do campo único (R06, p.72): a data `aaaa-mm-dd` do recorte de um dia. */
export function dataDoDia(dia: DiaDoGestor, hoje: string): string {
  if (dia === 'hoje') return hoje;
  if (dia === 'ontem') return somarDias(hoje, -1);
  return dia;
}

/** O que vai para o endereço quando se escolhe uma data no campo único: hoje e ontem pelo nome. */
export function diaDaData(data: string, hoje: string): DiaDoGestor | null {
  if (!ehData(data)) return null;
  if (data === hoje) return 'hoje';
  if (data === dataDoDia('ontem', hoje)) return 'ontem';
  return data;
}
